import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { useCallback, useState, useMemo, useEffect } from "react";
import { CallApiChangePass } from "../Api/Enforcement";
import { SwalSuccess } from "../Share/Swal";
import { login, changePass } from "../Api/SubUrl";
import { ShowCustomErrorModal } from "../Components/Atoms/CustomErrorModal";
import useReCaptcha from "./useReCaptcha";
import { handleClientLogout } from "../Share/Func/clientLogout";
import { verifyClientSession } from "../Share/Func/clientSession";

const ERROR_MESSAGES = {
  NETWORK_ERROR: "Thực hiện chưa thành công!\n Liên hệ để được hỗ trợ",
  RECAPTCHA_INVALID: "Đã xảy ra lỗi : \nreCAPTCHA không hợp lệ!",
  GENERIC_ERROR: "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ",
};

export default function useChangePassPage() {
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const {
    version,
    reCaptchaV2,
    isReCaptchaReady,
    isLoadReCaptChaV3,
    recaptchaLoading,
    recaptchaError,
    getReCaptchaToken,
    resetReCaptcha,
    isDefaultV3,
  } = useReCaptcha("changePass", changePass);

  useEffect(() => {
    let cancelled = false;

    verifyClientSession().then((user) => {
      if (cancelled) return;
      if (!user?.AccountName) {
        navigate(login, { replace: true });
        return;
      }

      setUser(user);
      setPageLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const schemaForm = yup.object().shape({
    accountName: yup.string().required("Vui lòng nhập tên tài khoản"),
    oldPassword: yup.string().required("Vui lòng nhập mật khẩu"),
    newPassword: yup
      .string()
      .required("Vui lòng nhập mật khẩu")
      .min(6, "Độ dài mật khẩu từ 6 đến 32 ký tự")
      .max(32, "Độ dài mật khẩu từ 6 đến 32 ký tự")
      .test(
        "is-same-password",
        "Mật khẩu mới phải khác với mật khẩu cũ",
        function (newPassword) {
          return newPassword !== this.parent.oldPassword;
        }
      ),
    rePassword: yup
      .string()
      .required("Vui lòng xác nhận lại mật khẩu")
      .test(
        "is-same-password",
        "Mật khẩu không trùng khớp",
        function (rePassword) {
          return rePassword === this.parent.newPassword;
        }
      ),
  });

  const { control, handleSubmit, reset } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: useMemo(
      () => ({
        accountName: "",
        oldPassword: "",
        newPassword: "",
        rePassword: "",
      }),
      []
    ),
    resolver: yupResolver(schemaForm),
  });

  useEffect(() => {
    if (!user?.AccountName) return;

    reset({
      accountName: user.AccountName,
      oldPassword: "",
      newPassword: "",
      rePassword: "",
    });
  }, [user, reset]);

  const handleChangePass = useCallback(
    async (formData) => {
      if (loading) return;
      try {
        const token = await getReCaptchaToken();
        setLoading(true);

        CallApiChangePass(
          token,
          formData,
          (res) => {
            setLoading(false);
            if (res.Status) {
              resetReCaptcha();
              const status = res.Status.status;
              const message = res.Status.data.Message;
              if (status === 400)
                ShowCustomErrorModal({
                  message: `<p>${message}</p>`,
                  title: "Đăng nhập thất bại",
                });
              else if (status === 403)
                ShowCustomErrorModal({
                  message: ERROR_MESSAGES.RECAPTCHA_INVALID,
                  title: "Xác thực thất bại",
                });
              else
                ShowCustomErrorModal({
                  message: ERROR_MESSAGES.GENERIC_ERROR,
                  title: "Đã xảy ra lỗi",
                });
            } else {
              if (res !== "success") {
                resetReCaptcha();
                ShowCustomErrorModal({
                  message: ERROR_MESSAGES.NETWORK_ERROR,
                  title: "Đã xảy ra lỗi",
                });
                return;
              }
              reset();
            SwalSuccess("Cập nhật thành công!");
            handleClientLogout(() => navigate(login));
            }
          }
        );
      } catch (error) {
        setLoading(false);
        resetReCaptcha();
        ShowCustomErrorModal({
          message: error.message || ERROR_MESSAGES.NETWORK_ERROR,
          title: "Cập nhật thất bại",
        });
      }
    },
    [getReCaptchaToken, loading, navigate, reset, resetReCaptcha]
  );

  const handleInvalid = useCallback((errors) => {
    const errorMessages = Object.values(errors)
      .map((err) => `<p>${err.message}</p>`)
      .join("");
    ShowCustomErrorModal({
      message: errorMessages,
      title: "Cập nhật thất bại",
    });
  }, []);

  return {
    loading,
    pageLoading,
    user,
    control,
    handleSubmit: handleSubmit(handleChangePass, handleInvalid),
    version,
    reCaptchaV2,
    isReCaptchaReady,
    isLoadReCaptChaV3,
    recaptchaLoading,
    recaptchaError,
    isDefaultV3,
  };
}
